import { useAuth } from '@clerk/nextjs';
import usePassiveLayoutEffect from '@react-hook/passive-layout-effect';
import Router from 'next/router';
import useSWR, { mutate, SWRConfiguration } from 'swr';

import { RejectedResult } from 'lib/request/Result';
import { joinUrl } from 'lib/request/utils';
import { User } from 'types';

const key = '/users/me';

export const USER_PREFETCH_URL = joinUrl(key);

export const mutateUser = (user?: User): Promise<User> => mutate(key, user);

type UserConfig = SWRConfiguration<User | null>;

export default function useParityUser(config?: UserConfig) {
  const { data, error, ...response } = useSWR<User | null, RejectedResult>(key, {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    ...config,
  });

  return {
    // Treat a cached `null` (left by /signout's `mutate(null, false)` in older flows,
    // or any other clear-by-write) the same as "no entry". Otherwise components that
    // synchronously read `user.foo` will crash on the first render after sign-in's
    // soft nav, before the redirect-effect in `useAuthenticatedParityUser` can fire.
    user: error?.status === 403 || data === null ? undefined : data,
    error,
    ...response,
  };
}

export function useAuthenticatedParityUser(config?: UserConfig) {
  const { isLoaded: clerkLoaded } = useAuth();
  const { user, ...data } = useParityUser(config);

  usePassiveLayoutEffect(() => {
    // Gate only the redirect on Clerk hydration — never the SWR key. Gating the key
    // makes the first render's lookup miss the SSR-populated fallback from gssp.ts
    // (cache['/users/me']), so `user` is undefined even for valid visitors and the
    // dashboard crashes on `user.accessType` before this effect can run.
    if (!clerkLoaded) {
      return;
    }
    if (!user) {
      if (process.env.NODE_ENV === 'development') {
        throw new Error('useAuthenticatedParityUser hook must be used in secured space.');
      }

      Router.replace('/signin');
    }
  }, [user, clerkLoaded]);

  return {
    ...data,
    user: user as User,
  };
}
